import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AlertCircle, CheckCircle, Clock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const [alertsRes, securityRes] = await Promise.all([
        api.get('/alerts?is_acknowledged=false'),
        api.get('/security/alerts?is_resolved=false')
      ]);
      setAlerts(alertsRes.data);
      setSecurityAlerts(securityRes.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.put(`/security/alerts/${alertId}/resolve`);
      toast.success('Security alert resolved');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || colors.info;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="alerts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Alerts & Monitoring</h1>
        <p className="text-gray-600 text-sm sm:text-base mt-1">System alerts and security notifications</p>
      </div>

      {/* Security Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-600" />
            Security Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {securityAlerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
              <p className="text-gray-600">No active security alerts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {securityAlerts.map((alert) => (
                <div key={alert.id} className="p-4 border border-red-200 bg-red-50 rounded-lg" data-testid="security-alert-card">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{alert.title}</h3>
                        <Badge className={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{alert.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {alert.source_ip && <span>Source: {alert.source_ip}</span>}
                        {alert.target_ip && <span>• Target: {alert.target_ip}</span>}
                        {alert.rule_matched && <span>• Rule: {alert.rule_matched}</span>}
                        <span>• {formatInCST(alert.created_at, 'MMM d, h:mm a')}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleResolve(alert.id)}
                      data-testid="resolve-button"
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            System Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
              <p className="text-gray-600">No active alerts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-4 bg-gray-50 rounded-lg" data-testid="alert-card">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{alert.title}</h3>
                        <Badge className={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                      </div>
                      {alert.description && (
                        <p className="text-sm text-gray-600 mb-2">{alert.description}</p>
                      )}
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatInCST(alert.created_at, 'MMM d, yyyy h:mm a')}
                        {alert.source && <span>• Source: {alert.source}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAcknowledge(alert.id)}
                      data-testid="acknowledge-button"
                    >
                      Acknowledge
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
